import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import GstAwareCostField from './GstAwareCostField';
import OverrideRevertBadge from './OverrideRevertBadge';

// Issue #193 — the checklist of a Shipping Service's Options, shared by
// QuotePage.js and SalesOrderPage.js (identical needs on both screens).
// selectedOptions: [{ shipping_option_id, cost, overridden }]. onChange
// hands back the whole replacement array, same "parent holds the buffer,
// child just reports edits" shape as QuotePage's own line handlers.
//
// computeDefaultCost mirrors backend/services/shippingOptionCostService.js's
// function of the same name — kept in sync deliberately, not shared, same
// "frontend mirrors backend" duplication already established for
// productCostPriceScheduleService.js/supplierExchangeRateScheduleService.js's
// date-schedule logic (there's no shared module between the two projects).
// Exported so QuotePage.js/SalesOrderPage.js's own applyCustomer can cost a
// Customer's default Option selections the same way ticking them by hand
// would, at the moment they cascade onto a brand-new document.
export function computeDefaultCost(option, valueBasisAmount) {
  if (option.pricing_mode === 'flat') {
    return Number(option.flat_fee) || 0;
  }
  const threshold = Number(option.scaled_free_threshold) || 0;
  const incrementSize = Number(option.scaled_increment_size) || 0;
  const ratePerIncrement = Number(option.scaled_rate_per_increment) || 0;
  const maxCap = Number(option.scaled_max_cap) || 0;
  if (incrementSize <= 0) return 0;
  const excess = Math.max(Number(valueBasisAmount) - threshold, 0);
  const raw = (excess / incrementSize) * ratePerIncrement;
  return Math.round(Math.min(raw, maxCap) * 10000) / 10000;
}

function ShippingOptionsPicker({
  shippingServiceId, quoteSubtotalExGst, lines, gstApplicable, gstRate, locked, selectedOptions, onChange,
}) {
  const [optionsForService, setOptionsForService] = useState([]);
  const optionsForServiceRef = useRef([]);
  const [clearedNotice, setClearedNotice] = useState('');

  // Fetches this Service's own Options, then reconciles the current
  // selection against them — dropping anything that doesn't actually
  // belong to this Service (a manual Service change) while leaving
  // anything that does alone. That membership check (rather than "did
  // shippingServiceId change, so blindly clear everything") is what makes
  // this safe when a Customer's default Service *and* matching default
  // Options are set together in the same update (see QuotePage.js's own
  // applyCustomer) — clearing unconditionally on any Service change would
  // immediately wipe out a cascade that was correct from the start.
  useEffect(() => {
    if (!shippingServiceId) {
      setOptionsForService([]);
      if (selectedOptions.length) {
        setClearedNotice('Options were cleared because the Shipping Service changed.');
        onChange([]);
      }
      return;
    }
    axios.get('/api/shipping-options', { params: { shipping_service_id: shippingServiceId, status: 'active' } })
      .then((res) => {
        const options = res.data.data || [];
        setOptionsForService(options);
        const validIds = new Set(options.map((o) => String(o.id)));
        const stillValid = selectedOptions.filter((sel) => validIds.has(String(sel.shipping_option_id)));
        if (stillValid.length !== selectedOptions.length) {
          setClearedNotice('Options were cleared because the Shipping Service changed.');
          onChange(stillValid);
        }
      })
      .catch(() => setOptionsForService([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shippingServiceId]);

  useEffect(() => {
    optionsForServiceRef.current = optionsForService;
  }, [optionsForService]);

  // Resolves the "Cost ex-GST" value basis via the server (product cost
  // isn't visible client-side) — mirrors the buffered-editing shape
  // /api/price-lookup already uses for unsaved line pricing.
  const fetchCostSubtotal = async () => {
    try {
      const res = await axios.post('/api/shipping-options/cost-subtotal', {
        lines: (lines || []).map((l) => ({ product_id: l.productId, qty: l.qty })),
      });
      return Number(res.data.data?.cost_subtotal_ex_gst) || 0;
    } catch (err) {
      return 0;
    }
  };

  const resolveBasisAmount = async (option) => (
    option.value_basis === 'cost_ex_gst' ? fetchCostSubtotal() : quoteSubtotalExGst
  );

  // Recomputes every selected Option's default cost when the value it's
  // measured against changes (a line added/removed/re-priced, or GST
  // context changing) — same "a fresh system lookup always supersedes a
  // prior override" rule QuotePage.js already applies to line price
  // overrides on qty/UoM change, applied here to the Option's own driving
  // input (the value basis) instead. Skipped on first mount so loading an
  // existing Quote/Sales Order doesn't immediately discard its saved costs.
  const linesFingerprint = JSON.stringify((lines || []).map((l) => [l.productId, l.qty]));
  const didMountRecompute = useRef(false);
  useEffect(() => {
    if (!didMountRecompute.current) { didMountRecompute.current = true; return; }
    if (!selectedOptions.length) return;
    let cancelled = false;
    (async () => {
      const options = optionsForServiceRef.current;
      let costBasis = null;
      const needsCostBasis = selectedOptions.some((sel) => {
        const opt = options.find((o) => String(o.id) === String(sel.shipping_option_id));
        return opt && opt.pricing_mode === 'scaled' && opt.value_basis === 'cost_ex_gst';
      });
      if (needsCostBasis) costBasis = await fetchCostSubtotal();
      if (cancelled) return;
      const updated = selectedOptions.map((sel) => {
        const opt = options.find((o) => String(o.id) === String(sel.shipping_option_id));
        if (!opt) return sel;
        const basis = opt.value_basis === 'cost_ex_gst' ? costBasis : quoteSubtotalExGst;
        return { shipping_option_id: sel.shipping_option_id, cost: computeDefaultCost(opt, basis) };
      });
      const changed = updated.some((u, i) => u.cost !== selectedOptions[i].cost || selectedOptions[i].overridden);
      if (changed) onChange(updated);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteSubtotalExGst, linesFingerprint]);

  const isSelected = (optionId) => selectedOptions.some((o) => String(o.shipping_option_id) === String(optionId));
  const selectionFor = (optionId) => selectedOptions.find((o) => String(o.shipping_option_id) === String(optionId));

  const handleToggle = async (option, checked) => {
    if (!checked) {
      onChange(selectedOptions.filter((o) => String(o.shipping_option_id) !== String(option.id)));
      return;
    }
    const basis = await resolveBasisAmount(option);
    const cost = computeDefaultCost(option, basis);
    onChange([...selectedOptions, { shipping_option_id: option.id, cost, overridden: false }]);
  };

  const handleCostCommit = (optionId, value) => {
    const cost = value === null || value === undefined ? 0 : Number(value);
    onChange(selectedOptions.map((o) => (
      String(o.shipping_option_id) === String(optionId) ? { ...o, cost, overridden: true } : o
    )));
  };

  const handleRevert = async (option) => {
    const basis = await resolveBasisAmount(option);
    const cost = computeDefaultCost(option, basis);
    onChange(selectedOptions.map((o) => (
      String(o.shipping_option_id) === String(option.id) ? { shipping_option_id: option.id, cost, overridden: false } : o
    )));
  };

  if (!shippingServiceId) return null;

  return (
    <div style={{ marginTop: 8 }}>
      {optionsForService.length === 0 ? null : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {optionsForService.map((option) => {
            const selected = selectionFor(option.id);
            return (
              <div key={option.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 220 }}>
                  <input
                    type="checkbox"
                    checked={isSelected(option.id)}
                    disabled={locked}
                    onChange={(e) => handleToggle(option, e.target.checked)}
                  />
                  {option.description}
                </label>
                {selected && (
                  <>
                    <GstAwareCostField
                      value={selected.cost}
                      onCommit={(val) => handleCostCommit(option.id, val)}
                      gstApplicable={gstApplicable}
                      gstRate={gstRate}
                      locked={locked}
                    />
                    {selected.overridden && !locked && (
                      <OverrideRevertBadge
                        onClick={() => handleRevert(option)}
                        title="Overridden — click to restore the calculated cost"
                      />
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
      {clearedNotice && (
        <div style={{ fontSize: 12, color: '#b45309', marginTop: 6 }}>{clearedNotice}</div>
      )}
    </div>
  );
}

export default ShippingOptionsPicker;
